import {
  X,
  FileText,
  MessageSquare,
  ArrowRight,
  Users,
  Award,
  Building,
  Target,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

interface AboutUsViewProps {
  isOpen: boolean;
  onClose: () => void;
}

const AboutUsView = ({ isOpen, onClose }: AboutUsViewProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen, onClose]);

  const aboutSections = [
    {
      title: "Leadership Team",
      description: "Meet our executive leadership and their achievements",
      icon: Users,
      section: "leadership",
      features: [
        "Executive Profiles & Experience",
        "Industry Recognition & Awards",
        "Strategic Vision & Expertise",
        "Leadership Contact Information",
      ],
    },
    {
      title: "Company Story",
      description: "Our journey, mission, and values driving innovation",
      icon: Building,
      section: "company-story",
      features: [
        "8+ Years of Excellence",
        "Global Operations & Reach",
        "ISO 27001 Certified",
        "Award-Winning Solutions",
      ],
    },
    {
      title: "Success Stories",
      description:
        "Explore our successful project implementations and transformations",
      icon: FileText,
      section: "case-studies",
      features: [
        "Banking Platform Modernization",
        "E-commerce Solution Success",
        "Healthcare System Integration",
        "Enterprise Digital Transformation",
      ],
    },
    {
      title: "Client Testimonials",
      description:
        "Hear what our clients say about their experience working with us",
      icon: MessageSquare,
      section: "testimonials",
      features: [
        "5-Star Client Reviews",
        "Success Stories & Outcomes",
        "Client Satisfaction Metrics",
        "Long-term Partnership Benefits",
      ],
    },
  ];

  const handleSectionClick = (section: string) => {
    // Close all dropdowns first
    onClose();

    // Determine the correct page based on section
    let targetPage = "/about";
    if (section === "case-studies" || section === "testimonials") {
      targetPage = "/case-studies";
    }

    // Navigate to the appropriate page
    navigate(targetPage);

    // Scroll to section after navigation
    setTimeout(() => {
      const element = document.querySelector(`#${section}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="fixed top-14 left-0 right-0 bg-background/95 backdrop-blur-sm border-b border-border shadow-xl z-[60]"
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => {}}
      onMouseLeave={onClose}
    >
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h3 className="text-lg font-bold text-foreground mb-1">About Us</h3>
        </div>

        {/* Mobile Layout */}
        <div className="block md:hidden">
          <div className="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto">
            {aboutSections.map((section, index) => (
              <button
                key={index}
                onClick={() => handleSectionClick(section.section)}
                className={`w-full group text-left px-3 py-3 text-sm text-foreground ${index % 2 === 0 ? 'hover:text-primary hover:bg-primary/5' : 'hover:text-secondary hover:bg-secondary/5'} transition-all duration-200 rounded-md border border-transparent ${index % 2 === 0 ? 'hover:border-primary/20' : 'hover:border-secondary/20'} flex items-center gap-3`}
              >
                <section.icon className={`h-4 w-4 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary'} flex-shrink-0`} />
                <div>
                  <div className={`font-medium text-sm leading-tight ${index % 2 === 0 ? 'group-hover:text-primary' : 'group-hover:text-secondary'} transition-colors`}>
                    {section.title}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 leading-tight">
                    {section.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
          {aboutSections.map((section, index) => (
            <div key={index} className="h-full flex flex-col">
              <button
                onClick={() => handleSectionClick(section.section)}
                className={`w-full group text-left px-3 py-4 text-sm text-foreground ${index % 2 === 0 ? 'hover:text-primary hover:bg-primary/5' : 'hover:text-secondary hover:bg-secondary/5'} transition-all duration-200 rounded-md border border-transparent ${index % 2 === 0 ? 'hover:border-primary/20' : 'hover:border-secondary/20'} h-full`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <section.icon className={`h-5 w-5 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary'} flex-shrink-0`} />
                  <div className={`font-medium text-sm leading-tight ${index % 2 === 0 ? 'group-hover:text-primary' : 'group-hover:text-secondary'} transition-colors`}>
                    {section.title}
                  </div>
                </div>
                <div className="text-xs text-muted-foreground leading-tight pl-8">
                  {section.description}
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AboutUsView;
